import {
  Analysis,
  DashboardConfigurationDependencies,
  Granularity,
} from '../../../../dashboard-config/dashboard-config.model';
import { DashboardFilter } from '../../../../filters/dashboard_filter';
import {
  DashboardStateSingleSelectFilter,
  DashboardStatesMultiSelectFilter,
  DashboardSubMarketSingleSelectFilter,
  DashboardSubMarketsMultiSelectFilter,
  DashboardWzClassSingleSelectFilter,
  DashboardWzClassesMultiSelectFilter,
} from '../../../../filters/dashboard_filter_implementations';

export const FORECAST_COMPARISON_SHELL_ID = 'forecast-comparison' as const;

export type DashboardForecastComparisonShell =
  | 'bundesvergleichTeilmarkte'
  | 'bundesvergleichWzKlassen'
  | 'marktvergleichTeilmarkte'
  | 'marktvergleichWzKlassen';

export interface DashboardForecastComparisonShellProfile {
  id: typeof FORECAST_COMPARISON_SHELL_ID;
  variant: string;
  granularityId: Granularity;
  /**
   * When set, {@link datasetName} is composed with this granularity while
   * {@link dashboardId} still uses {@link granularityId} (bundesvergleich WZ).
   */
  datasetGranularityId?: Granularity;
  analysisId: Analysis;
  enablePeerStateDefault: boolean;
  sortingKey: string;
  /** Overrides the granularity's chart heading words (Bundesvergleich WZ-Klassen names its WZ class). */
  chartHeadingGranularity?: (filterValues: Map<string, any>) => string;
  buildGranularityFilters: (deps: DashboardConfigurationDependencies) => DashboardFilter<any>[];
}

export const dashboardForecastComparisonShellProfiles: Record<DashboardForecastComparisonShell, DashboardForecastComparisonShellProfile> = {
  bundesvergleichTeilmarkte: {
    id: FORECAST_COMPARISON_SHELL_ID,
    variant: 'bundesvergleich-teilmarkte',
    granularityId: Granularity.States,
    analysisId: Analysis.Forecast,
    enablePeerStateDefault: true,
    sortingKey: 'state_name',
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelected: true,
      }),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {}),
    ],
  },
  bundesvergleichWzKlassen: {
    id: FORECAST_COMPARISON_SHELL_ID,
    variant: 'bundesvergleich-wz-klassen',
    granularityId: Granularity.States,
    datasetGranularityId: Granularity.WzClasses,
    analysisId: Analysis.Forecast,
    enablePeerStateDefault: true,
    sortingKey: 'state_name',
    chartHeadingGranularity: (fv) => `IN ${fv.get('wz_class')} ${fv.get('sub_market')} NACH BUNDESLÄNDERN`,
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        allInitiallySelected: true,
      }),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
        excluded: deps.sumSubMarketIds,
      }),
      new DashboardWzClassSingleSelectFilter(deps.localDatasetsService, {
        width: '275px',
      }),
    ],
  },
  marktvergleichTeilmarkte: {
    id: FORECAST_COMPARISON_SHELL_ID,
    variant: 'marktvergleich-teilmarkte',
    granularityId: Granularity.SubMarkets,
    analysisId: Analysis.Forecast,
    enablePeerStateDefault: false,
    sortingKey: 'sub_market_name',
    buildGranularityFilters: (deps) => [
      new DashboardStateSingleSelectFilter(deps.localDatasetsService, {}),
      new DashboardSubMarketsMultiSelectFilter(deps.localDatasetsService, {}),
    ],
  },
  marktvergleichWzKlassen: {
    id: FORECAST_COMPARISON_SHELL_ID,
    variant: 'marktvergleich-wz-klassen',
    granularityId: Granularity.WzClasses,
    analysisId: Analysis.Forecast,
    enablePeerStateDefault: false,
    sortingKey: 'wz_name',
    buildGranularityFilters: (deps) => [
      new DashboardStateSingleSelectFilter(deps.localDatasetsService, {}),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {
        excluded: deps.sumSubMarketIds,
      }),
      new DashboardWzClassesMultiSelectFilter(deps.localDatasetsService, {}),
    ],
  },
};
